import { createContext, useContext, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  PawPrint,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Rocket,
} from "lucide-react";

const LeadCaptureContext = createContext(null);

export function useLeadCapture() {
  const ctx = useContext(LeadCaptureContext);
  if (!ctx) throw new Error("useLeadCapture must be used within LeadCaptureProvider");
  return ctx;
}

const INITIAL = {
  name: "",
  company: "",
  businessType: "",
  email: "",
  dogCount: "",
};

const BUSINESS_TYPES = ["Hundpensionat", "Hunddagis", "Hundrastare", "Annat"];
const DOG_COUNTS = ["1-20", "21-50", "50+"];

const LEADS_ENDPOINT =
  process.env.REACT_APP_LEADS_ENDPOINT || "https://hundify.com/api/leads";

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Namn krävs";
  if (!form.company.trim()) errors.company = "Företagsnamn krävs";
  if (!form.businessType) errors.businessType = "Välj typ av verksamhet";
  if (!form.email.trim()) errors.email = "E-post krävs";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Ogiltig e-postadress";
  if (!form.dogCount) errors.dogCount = "Välj antal hundar";
  return errors;
}

export function LeadCaptureProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("Allmän förfrågan");
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const api = useMemo(
    () => ({
      openLeadCapture: (src = "Allmän förfrågan") => {
        setSource(src);
        setStatus("idle");
        setErrorMsg("");
        setErrors({});
        setForm(INITIAL);
        setOpen(true);
      },
      closeLeadCapture: () => setOpen(false),
    }),
    []
  );

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setStatus("submitting");
    setErrorMsg("");

    try {
      const res = await fetch(LEADS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          businessType: form.businessType,
          email: form.email.trim().toLowerCase(),
          dogCount: form.dogCount,
          source,
          submittedAt: new Date().toISOString(),
        }),
      });

      if (!res.ok) {
        let detail = "";
        try {
          const data = await res.json();
          detail = data?.message || data?.error || "";
        } catch {
          /* ignore */
        }
        throw new Error(
          detail || `Servern svarade med status ${res.status}. Försök igen om en stund.`
        );
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      const message = err?.message?.includes("Failed to fetch")
        ? "Kunde inte nå servern. Kontrollera din anslutning och försök igen."
        : err?.message || "Något gick fel. Försök igen om en stund.";
      setErrorMsg(message);
    }
  };

  return (
    <LeadCaptureContext.Provider value={api}>
      {children}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="lead-capture-dialog"
          className="sm:max-w-[600px] rounded-3xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-0 overflow-hidden gap-0"
        >
          {status === "success" ? (
            <SuccessScreen onClose={() => setOpen(false)} source={source} />
          ) : (
            <>
              {/* Header band */}
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 text-white overflow-hidden">
                <div className="absolute inset-0 bg-dotted opacity-20 pointer-events-none" />
                <div className="relative flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 shadow-lg">
                    <Rocket className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-300">
                      {source}
                    </div>
                    <DialogTitle className="font-display text-xl sm:text-2xl font-bold mt-1 leading-tight">
                      Hundify 2.0 — Ansök om tidig åtkomst
                    </DialogTitle>
                  </div>
                </div>
                <DialogDescription className="mt-3 text-slate-300 text-sm">
                  Fyll i några snabba uppgifter så hör vi av oss inom 24 timmar med
                  nästa steg.
                </DialogDescription>
              </div>

              <form onSubmit={submit} className="p-6 space-y-4" noValidate>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Namn" required error={errors.name}>
                    <Input
                      data-testid="lead-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Anna Andersson"
                      className="h-11"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Företagsnamn" required error={errors.company}>
                    <Input
                      data-testid="lead-company"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Tassträff AB"
                      className="h-11"
                      autoComplete="organization"
                    />
                  </Field>
                </div>

                <Field label="Typ av verksamhet" required error={errors.businessType}>
                  <select
                    data-testid="lead-business-type"
                    value={form.businessType}
                    onChange={update("businessType")}
                    className="flex h-11 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:opacity-50"
                  >
                    <option value="" disabled>
                      Välj typ av verksamhet
                    </option>
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="E-postadress" required error={errors.email}>
                  <Input
                    data-testid="lead-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="anna@tasstraff.se"
                    className="h-11"
                    autoComplete="email"
                  />
                </Field>

                <Field label="Antal hundar ni hanterar" required error={errors.dogCount}>
                  <select
                    data-testid="lead-dog-count"
                    value={form.dogCount}
                    onChange={update("dogCount")}
                    className="flex h-11 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  >
                    <option value="" disabled>
                      Välj antal hundar
                    </option>
                    {DOG_COUNTS.map((c) => (
                      <option key={c} value={c}>
                        {c} hundar
                      </option>
                    ))}
                  </select>
                </Field>

                {status === "error" && (
                  <div
                    data-testid="lead-error"
                    role="alert"
                    className="flex items-start gap-2 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 px-3 py-2.5 text-sm text-rose-700 dark:text-rose-300"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setOpen(false)}
                    disabled={status === "submitting"}
                    className="rounded-xl h-11"
                    data-testid="lead-cancel"
                  >
                    Avbryt
                  </Button>
                  <Button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="lead-submit"
                    className="rounded-xl h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_6px_18px_-4px_rgba(16,185,129,0.55)] disabled:opacity-70"
                  >
                    {status === "submitting" ? "Skickar…" : "Ansök om tidig åtkomst"}
                  </Button>
                </DialogFooter>

                <p
                  data-testid="lead-early-access-note"
                  className="text-xs leading-relaxed text-slate-600 dark:text-slate-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-xl px-4 py-3"
                >
                  Vi migrerar just nu till vår nya molnplattform 2.0. Genom att
                  registrera dig får du prioriterad tillgång, 50% rabatt vid
                  lanseringen och en personlig genomgång av systemet så snart vi
                  öppnar dörrarna.
                </p>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1 text-xs text-slate-500 dark:text-slate-400">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                    GDPR-säker
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
                    Svar inom 24 h
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <PawPrint className="h-3.5 w-3.5 text-emerald-500" />
                    50% lanserings­rabatt
                  </span>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </LeadCaptureContext.Provider>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
        {label} {required && <span className="text-emerald-500">*</span>}
      </Label>
      {children}
      {error && (
        <p className="text-xs text-rose-600 dark:text-rose-400 font-medium">{error}</p>
      )}
    </div>
  );
}

function SuccessScreen({ onClose, source }) {
  return (
    <div data-testid="lead-success-screen" className="relative overflow-hidden">
      {/* Decorative gradient */}
      <div className="absolute -top-32 -right-20 h-64 w-64 blob-emerald opacity-80 pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 h-56 w-56 blob-slate opacity-60 pointer-events-none" />

      <div className="relative p-10 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_12px_30px_-8px_rgba(16,185,129,0.65)] ring-8 ring-emerald-500/10">
          <CheckCircle2 className="h-10 w-10" strokeWidth={2.2} />
        </div>

        <DialogTitle className="font-display mt-7 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
          Tack!
        </DialogTitle>

        <p className="mt-4 max-w-md mx-auto text-base text-slate-600 dark:text-slate-300 leading-relaxed">
          Din ansökan är mottagen. Vårt team hör av sig inom kort med nästa steg
          i din väg till Hundify 2.0.
        </p>

        <div className="mt-7 mx-auto max-w-sm rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-4 text-left">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-700 dark:text-emerald-300">
            Vad händer nu?
          </div>
          <ul className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-200">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
              Bekräftelsemail skickat till din inbox
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
              Personlig genomgång bokas inom 24 timmar
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
              50% lanseringsrabatt reserverad för dig
            </li>
          </ul>
        </div>

        <div className="mt-8 text-xs text-slate-400 dark:text-slate-500">
          Referens: <span className="font-mono">{source}</span>
        </div>

        <Button
          onClick={onClose}
          data-testid="lead-success-close"
          className="mt-7 h-11 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white font-semibold"
        >
          Stäng <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
